import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { CHALLENGE_SENT_EVENT } from "@/lib/games";
import { CLOCKS, clockLabel } from "@/lib/clocks";
import { Button } from "@/components/ui/button";
import Avatar from "@/components/Avatar";
import PlayerLink from "@/components/PlayerLink";
import { BOARD_NAMES } from "@/components/YourGames";
import BoardChoice from "@/components/BoardChoice";
import UnbrokenChainToggle from "@/components/UnbrokenChainToggle";
import SameTilesToggle from "@/components/SameTilesToggle";
import { MagnifyingGlass, Sword, X } from "@phosphor-icons/react";
import { toast } from "sonner";

// Timed games: both players play each turn at once (the default), or take turns.
const TURN_CHOICES = [
  { id: "same", simultaneous: true, label: "Same time", sub: "Both play each turn at once" },
  { id: "take", simultaneous: false, label: "Take turns", sub: "One player at a time" },
];

function seen(iso) {
  if (!iso) return "";
  const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 600) return "Active just now";
  if (s < 86400) return "Active today";
  const d = Math.floor(s / 86400);
  return d === 1 ? "Active yesterday" : `Active ${d}d ago`;
}

// Find opponent (Jack, 2026-10-16): a search bar over a list of every
// player, each challengeable directly. Pick the player, then the board.
// `timed`: the lobby's Timed game card (2026-10-22) lists only players online
// now and sends a challenge with a chess clock picked here (2026-10-23).
// `board`: a set board, so there is no board to pick, just Send (Letterheist,
// 2026-10-25).

export default function ChallengePlayers({ onSent, timed = false, board = null }) {
  const [players, setPlayers] = useState(null); // null = loading
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState("");
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testUnbroken, setTestUnbroken] = useState(false);
  const [sameTiles, setSameTiles] = useState(false);
  const [clock, setClock] = useState("7");
  // Timed games (Jack, 2026-11-02): both players play each turn at the same
  // time, or take turns as before. Same time is the default.
  const [simultaneous, setSimultaneous] = useState(true);

  const load = async () => {
    setFailed(false);
    try {
      const { data } = await api.get(timed ? "/users/directory?timed=1" : "/users/directory", { timeout: 15000 });
      // Whoever is online now first, like the Raider Races list (Jack,
      // 2026-10-29); the rest stay most recently active first.
      setPlayers([...(data || [])].sort((a, b) => !a.online - !b.online));
    } catch {
      setFailed(true);
    }
  };
  useEffect(() => { load(); }, []);
  // Refreshed so the Online dots stay current.
  useEffect(() => {
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = players || [];
    if (!q) return list;
    // Names starting with the search first, then names containing it.
    const starts = list.filter((u) => u.username.toLowerCase().startsWith(q));
    const contains = list.filter((u) => !u.username.toLowerCase().startsWith(q) && u.username.toLowerCase().includes(q));
    return [...starts, ...contains];
  }, [players, query]);

  const challenge = async (username, board) => {
    setBusy(true);
    try {
      const { data: r } = await api.post("/challenges", {
        username, layout: board,
        ...(timed ? { clock, simultaneous } : {}),
        ...(testUnbroken ? { test_rules: "unbroken" } : {}),
        ...(sameTiles ? { same_tiles: true } : {}),
      });
      if (r.status === "incoming") {
        toast.info(`${r.challenge.from_username} has already ${timed ? "sent you a timed challenge" : "challenged you"}. Accept it in Your games.`);
      } else if (r.status === "pending") {
        toast.info(`You've already ${timed ? "sent a timed challenge to" : "challenged"} ${r.challenge.to_username}.`);
      } else {
        toast.success(`${BOARD_NAMES[r.challenge.layout] || "Game"}${timed ? ` ${clockLabel(clock)}${simultaneous ? " same-time" : ""}` : ""} challenge sent to ${r.challenge.to_username}.`);
      }
      window.dispatchEvent(new Event(CHALLENGE_SENT_EVENT));
      setPicked(null);
      setTestUnbroken(false);
      onSent?.();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Couldn't send the challenge");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col min-h-0 flex-1" data-testid="challenge-players">
      <div className="relative flex-none">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setPicked(null); }}
          placeholder="Search players by username"
          autoComplete="off"
          autoCapitalize="none"
          spellCheck={false}
          maxLength={40}
          className="w-full h-11 pl-9 pr-3 rounded-full border border-border bg-background text-sm outline-none focus:border-brand"
          data-testid="challenge-search-input"
        />
      </div>

      {picked && (
        <div className="mt-3 min-h-0 flex-1 overflow-y-auto rounded-xl border border-brand/40 bg-brand/5 p-3" data-testid="challenge-pick-board">
          <div className="flex items-center justify-between gap-2 mb-2.5">
            <div className="flex items-center gap-2 min-w-0">
              <Avatar userId={picked.id} username={picked.username} version={picked.avatar_v ?? null} className="w-8 h-8 text-xs" fallbackClassName="bg-muted text-muted-foreground" />
              <div className="text-sm min-w-0">
                Challenge <span className="font-semibold break-words">{picked.username}</span>{timed || board ? "" : " on which board?"}
              </div>
            </div>
            <button type="button" onClick={() => setPicked(null)} className="text-muted-foreground hover:text-foreground flex-none" aria-label="Cancel" data-testid="challenge-pick-cancel">
              <X size={16} />
            </button>
          </div>
          <UnbrokenChainToggle className="mb-2.5" checked={testUnbroken} onChange={setTestUnbroken} testId="challenge-test-unbroken" />
          <SameTilesToggle className="mb-2.5" checked={sameTiles} onChange={setSameTiles} testId="challenge-same-tiles" />
          {timed && (
            <>
              <p className="text-[11px] uppercase tracking-[0.15em] font-extrabold text-muted-foreground mb-1.5">Clock</p>
              <div className="grid grid-cols-3 gap-2 mb-3" data-testid="clock-choice">
                {CLOCKS.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setClock(c.id)}
                    className={`text-center py-2.5 px-1 rounded-xl border-2 transition-colors ${
                      clock === c.id ? "border-amber-500 bg-amber-500/10 text-amber-500" : "border-border hover:border-foreground/40"
                    }`}
                    data-testid={`clock-${c.id}`}
                  >
                    <div className="font-display font-extrabold text-[15px] leading-tight">{c.label}</div>
                    <div className="text-[10px] opacity-70 mt-0.5">{c.sub}</div>
                  </button>
                ))}
              </div>
              <p className="text-[11px] uppercase tracking-[0.15em] font-extrabold text-muted-foreground mb-1.5">Turns</p>
              <div className="grid grid-cols-2 gap-2 mb-3" data-testid="turns-choice">
                {TURN_CHOICES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setSimultaneous(t.simultaneous)}
                    className={`text-center py-2.5 px-1 rounded-xl border-2 transition-colors ${
                      simultaneous === t.simultaneous ? "border-amber-500 bg-amber-500/10 text-amber-500" : "border-border hover:border-foreground/40"
                    }`}
                    data-testid={`turns-${t.id}`}
                  >
                    <div className="font-display font-extrabold text-[15px] leading-tight">{t.label}</div>
                    <div className="text-[10px] opacity-70 mt-0.5">{t.sub}</div>
                  </button>
                ))}
              </div>
              {!board && <p className="text-[11px] uppercase tracking-[0.15em] font-extrabold text-muted-foreground mb-1.5">Board</p>}
            </>
          )}
          {board ? (
            <Button
              disabled={busy}
              onClick={() => challenge(picked.username, board)}
              className="w-full h-11 rounded-xl bg-brand hover:bg-brand-hover text-brand-foreground font-bold"
              data-testid="challenge-send"
            >
              <Sword size={16} weight="bold" className="mr-1.5" /> Send challenge
            </Button>
          ) : (
            <BoardChoice disabled={busy} onPick={(id) => challenge(picked.username, id)} testIdPrefix="challenge-board" />
          )}
        </div>
      )}

      {/* The board write-ups need the room, so the list steps aside while picking. */}
      <div className={`mt-3 min-h-0 flex-1 overflow-y-auto overscroll-contain touch-pan-y [-webkit-overflow-scrolling:touch] rounded-xl border border-border bg-background ${picked ? "hidden" : ""}`} data-testid="challenge-player-list">
        {players === null ? (
          <div className="px-4 py-6 text-sm text-muted-foreground text-center">
            {failed ? (
              <>Couldn't load players.{" "}
                <button type="button" onClick={load} className="font-semibold text-brand underline">Retry</button>
              </>
            ) : "Loading players…"}
          </div>
        ) : shown.length === 0 ? (
          <div className="px-4 py-6 text-sm text-muted-foreground text-center">{timed ? "No one else is here yet." : "No players found."}</div>
        ) : (
          <ul className="divide-y divide-border">
            {shown.map((u) => (
              <li key={u.id} className={`flex items-center justify-between gap-3 px-3 py-2.5 ${picked?.id === u.id ? "bg-brand/5" : ""}`}>
                <div className="flex items-center gap-2.5 min-w-0 flex-1">
                  <div className="relative flex-none">
                    <Avatar userId={u.id} username={u.username} version={u.avatar_v ?? null} className="w-9 h-9 text-sm" fallbackClassName="bg-muted text-muted-foreground" />
                    {u.online && <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-green-500 ring-2 ring-background" data-testid={timed ? "timed-online-dot" : "online-dot"} />}
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold break-words">
                      <PlayerLink id={u.id}>{u.username}</PlayerLink>
                      {u.is_guest && (
                        <span className="ml-1.5 align-middle text-[10px] font-medium px-1.5 py-px rounded-full border border-border text-muted-foreground" data-testid="guest-tag">Guest</span>
                      )}
                    </div>
                    {timed ? (
                      <div className={`text-[11px] ${u.online ? "text-green-600 dark:text-green-400 font-medium" : "text-muted-foreground"}`}>
                        {u.online ? "Online" : "Offline · gets a notification"}
                      </div>
                    ) : (
                    <div className="text-[11px] text-muted-foreground">
                      {u.elo_rating} Elo{u.online ? <> · <span className="text-green-600 dark:text-green-400 font-medium">Online</span></>
                        : u.last_seen_at ? ` · ${seen(u.last_seen_at)}` : ""}
                    </div>
                    )}
                  </div>
                </div>
                <Button
                  size="sm"
                  onClick={() => setPicked(u)}
                  className="rounded-full bg-brand hover:bg-brand-hover text-brand-foreground flex-none"
                  data-testid={`challenge-${u.username}-btn`}
                >
                  <Sword size={14} weight="bold" className="mr-1" /> Challenge
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
